import {Injectable} from '@angular/core';
import {UtilService} from './util.service';
import {HttpClient, HttpParams, HttpRequest} from '@angular/common/http';
import {Observable} from 'rxjs/Observable';
import {Station} from '../model/StationModel';
import {CreateReservationsResponse, Price, SearchData, SearchDataDto, SearchDataGroup} from '../model/SearchData';
import {IssuedTicket} from '../model/IssuedTicket';
import {UserActivityReport} from '../model/UserActivity';
import {CurrentUser} from '../model/CurrentUser';
import {AvailabilityReport} from '../model/AvailabilityReport';
import {Seat} from '../model/BusLayout';
import {Currency} from '../model/Currency';
import {AgencyTicketReservation} from '../model/AgencyTicketReservation';
import {Luggage} from '../model/Luggage';
import {createHistoryBookingFromBE, HistoryBooking, HistoryBookingBE} from '../model/History';
import {MatSnackBar} from '@angular/material';
import {
  ConfirmReservationRequest,
  ConfirmReservationsAndPayRequest,
  ConfirmReservationsRequest
} from '../model/ConfirmReservationsRequest';
import {RouteLine, RouteLineBE} from '../model/RouteLine';
import * as moment from 'moment';
import {RouteLineTimetableDescription, RouteLineTimetableDescriptionBE} from '../model/RouteLineTimetableDescription';
import {Bus} from '../model/Bus';
import {Invoice} from '../model/Invoice';
import {PrivilegedVoyageCard} from '../model/PrivilegedVoyageCard';
import {SharedDataService} from './shared-data.service';

@Injectable()
export class TicketBookingService {
  constructor(private http: HttpClient,
              private sharedDataService: SharedDataService,
              private utilService: UtilService,
              public snackBar: MatSnackBar) {
  }

  search(searchDataDto: SearchDataDto, searchData: SearchData): Observable<SearchDataGroup[]> {
    const url = this.utilService.getApiUrl('trip/searchSegmented');
    return this.http.post(url, searchDataDto)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data.trips.map(value => new SearchDataGroup(value.group, searchData));
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  searchDirectOnly(searchDataDto: SearchDataDto, searchData: SearchData): Observable<SearchDataGroup[]> {
    const url = this.utilService.getApiUrl('trip/search');
    return this.http.post(url, searchDataDto)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data.trips.map(value => new SearchDataGroup(value.group, searchData));
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  getStations(): Observable<Station[]> {
    const url = this.utilService.getApiUrl('stations/');
    return this.http.get(url)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data.stations.map(value => new Station(value));
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  newReservationGroup(): Observable<any> {
    const url = this.utilService.getApiUrl('reservation/newReservationGroup');
    return this.http.get(url)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data.reservationGroupId;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  createReservations(dataObj: any): Observable<CreateReservationsResponse[]> {
    const url = this.utilService.getApiUrl('reservation/createReservations');
    return this.http.post(url, dataObj)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data.map(value => new CreateReservationsResponse(value));
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  createReservation(dataObj: any): Observable<CreateReservationsResponse[]> {
    const url = this.utilService.getApiUrl('reservation/createReservation');
    return this.http.post(url, dataObj)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return [new CreateReservationsResponse(response.data)];
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  swapSeats(dataObj: any): Observable<any> {
    const url = this.utilService.getApiUrl('reservation/swapSeats/');
    return this.http.post(url, dataObj)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  prices(dataObj: any): Observable<Price[]> {
    const url = this.utilService.getApiUrl('prices/trip/prices');
    return this.http.post(url, dataObj)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data.prices.map(value => new Price(value));
          } else {
            this.snackBar.open(response.message);
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          console.error(error);
          return error;
        },
      );
  }

  pricesSegments(dataObj: any): Observable<Price[]> {
    const url = this.utilService.getApiUrl('prices/trip/pricesSegments');
    return this.http.post(url, dataObj)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data.prices.map(value => new Price(value));
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  setPrice(dataObj: any): Observable<any> {
    const url = this.utilService.getApiUrl('prices/price');
    return this.http.post(url, dataObj)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => error,
      );
  }

  setPriceSegmented(dataObj: any): Observable<any> {
    const url = this.utilService.getApiUrl('prices/priceSegmented');
    return this.http.post(url, dataObj)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => error,
      );
  }

  setLuggage(luggage: Luggage): Observable<any> {
    const url = this.utilService.getApiUrl('luggage/createForTickets');
    try {
      return this.http.post(url, luggage)
        .map(
          (response: any) => {
            if (response.status === 0) {
              return response;
            } else {
              throw Observable.throw(response);
            }
          },
          (error: any) => {
            return error;
          },
        );
    } catch (e) {
      console.error(e);
    }
  }

  isLuggageEditable(ticketId: number): Observable<any> {
    const url = this.utilService.getApiUrl('luggage/enabled/' + ticketId);
    try {
      return this.http.get(url)
        .map(
          (response: any) => {
            if (response.status === 0) {
              return response.data;
            } else {
              throw Observable.throw(response);
            }
          },
          (error: any) => error,
        );
    } catch (e) {
      console.error(e);
    }
  }

  getLuggagePrices(bookingId: number): Observable<any> {
    const url = this.utilService.getApiUrl('luggage/getPricesForBooking');
    return this.http.post(url, {bookingId: bookingId})
      .map((response: any) => {
          if (response.status === 0) {
            return response;
          } else {
            throw Observable.throw(response);
          }
        },
        (error) => {
          console.error(error);
          return error;
        },
      );
  }

  updateReservationData(dataObj: any): Observable<any> {
    const url = this.utilService.getApiUrl('reservation/updateReservationData');
    return this.http.post(url, dataObj)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  saveReservation(dataObj: any): Observable<any> {
    const url = this.utilService.getApiUrl('reservation/saveReservation');
    return this.http.post(url, dataObj)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  confirmReservation(dataObj: any): Observable<any> {
    const url = this.utilService.getApiUrl('reservation/confirmReservation');
    return this.http.post(url, {...dataObj})
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  confirmReservations(request: ConfirmReservationsRequest): Observable<string[]> {
    const url = this.utilService.getApiUrl('reservation/confirmReservations');
    return this.http.post(url, request)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  confirmReservationsAndPay(request: ConfirmReservationsAndPayRequest): Observable<number[]> {
    const url = this.utilService.getApiUrl('reservation/confirmReservationsAndPay');
    return this.http.post(url, request)
      .map(
        (response: any) => {
          if (response.status === 0) {
            this.sharedDataService.secondsUtilExpiration = 0;
            return response.data;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  confirmReservationAgencyTicket(request: ConfirmReservationRequest): Observable<AgencyTicketReservation> {
    const url = this.utilService.getApiUrl('reservation/confirmReservation/agencyTicket');
    return this.http.post(url, request)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return new AgencyTicketReservation(response.data);
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  confirmReservationsAndPayAgencyTicket(request: ConfirmReservationsAndPayRequest): Observable<number[]> {
    const url = this.utilService.getApiUrl('reservation/confirmReservationsAndPay/agencyTicket');

    return this.http.post(url, request)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        }
      );
  }

  paid(dataObj: any): Observable<any> {
    const url = this.utilService.getApiUrl('reservation/paid');
    return this.http.post(url, dataObj)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  reservationPrice(dataObj: any): Observable<any> {
    const url = this.utilService.getApiUrl('reservation/price');
    return this.http.post(url, dataObj)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  deleteReservations(dataObj: any): Observable<any> {
    const url = this.utilService.getApiUrl('reservation/deleteReservations');
    return this.http.post(url, dataObj)
      .map(
        (response: any) => {
          if (response.status === 0) {
            this.sharedDataService.secondsUtilExpiration = 0;
            return response;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  deleteReservation(dataObj: any): Observable<any> {
    const url = this.utilService.getApiUrl('reservation/deleteReservation');
    return this.http.post(url, dataObj)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  issuedDrives(ticketId: string, bpId: number): Observable<IssuedTicket[]> {
    const url = this.utilService.getApiUrl('reservation/issuedDrives?premiseid=' + bpId + '&reservationId=' + ticketId);
    return this.http.get(url)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data.map(value => new IssuedTicket(value));
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  getHistory(reservationId?: string): Observable<HistoryBooking[]> {
    const url = this.utilService.getApiUrl('reservation/history');

    let params = new HttpParams();
    params = params.set('reservationId', reservationId);


    return this
      .http.get(url, {params})
      .map(
        (response: any) => {
          if (response.status === 0) {
            const data: HistoryBookingBE[] = response.data;
            return data.map(createHistoryBookingFromBE);
          } else {
            throw Observable.throw(response);
          }
        },
        (error: Error) => error,
      );
  }

  searchHistory(
    date: Date,
    routeLineTimetableId: number,
    busId: number,
  ): Observable<HistoryBooking[]> {
    const url = this.utilService.getApiUrl('reservation/history/search');

    let params = new HttpParams();
    params = params.set('date', moment(date).format('YYYY-MM-DD'));
    params = params.set('routeLineTimetableId', routeLineTimetableId.toString());
    params = params.set('busId', busId.toString());


    return this
      .http.get(url, {params})
      .map(
        (response: any) => {
          if (response.status === 0) {
            const data: HistoryBookingBE[] = response.data;
            return data.map(createHistoryBookingFromBE);
          } else {
            throw Observable.throw(response);
          }
        },
        (error: Error) => error,
      );
  }

  getRouteLinesForHistoryFilter(departure: Date): Observable<RouteLine[]> {
    const url = this.utilService.getApiUrl('routelines/');

    let params = new HttpParams();
    params = params.set('departure', moment(departure).format('YYYY-MM-DD'));


    return this.http.get(url, {params}).map(
      (response: any) => {
        if (response.status === 0) {
          return response.data.map((plain: RouteLineBE) => RouteLine.fromBE(plain));
        } else {
          throw Observable.throw(response);
        }
      },
    );
  }

  getSecondsUntilExpiration(reservationGroupId: number): Observable<any> {
    const url = this.utilService.getApiUrl('reservation/secondsUntilExpiration');

    let params = new HttpParams();
    params = params.set('id', String(reservationGroupId));


    return this.http.get(url, {params}).map(
      (response: any) => {
        if (response.status === 0) {
          return response.data;
        } else {
          throw Observable.throw(response);
        }
      },
    );
  }

  getRouteLineTimetablesForHistoryFilter(
    departure: Date,
    routeLineId: number,
  ): Observable<RouteLineTimetableDescription[]> {
    const url = this.utilService.getApiUrl(`routelines/${routeLineId}/route-line-timetable`);

    let params = new HttpParams();
    params = params.set('departure', moment(departure).format('YYYY-MM-DD'));

    return this.http.get(url, {params}).map(
      (response: any) => {
        if (response.status === 0) {
          return response.data.map(
            (plain: RouteLineTimetableDescriptionBE) => RouteLineTimetableDescription.fromBE(plain),
          );
        } else {
          throw Observable.throw(response);
        }
      },
    );
  }

  getBusesForHistoryFilter(
    departure: Date,
    routeLineTimetableId: number,
  ): Observable<RouteLineTimetableDescription[]> {
    const url = this.utilService.getApiUrl(`buses/search`);

    let params = new HttpParams();
    params = params.set('departure', moment(departure).format('YYYY-MM-DD'));
    params = params.set('routeLineTimetableId', routeLineTimetableId.toString());

    return this.http.get(url, {params}).map(
      (response: any) => {
        if (response.status === 0) {
          return response.data.map(plain => new Bus(plain));
        } else {
          throw Observable.throw(response);
        }
      },
    );
  }

  bookingInvoices(bookingId: any): Observable<any[]> {
    const url = this.utilService.getApiUrl('reservation/booking/' + bookingId + '/invoices');
    return this.http.get(url)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  isAgencyTicketUsed(dataObj: any): Observable<any> {
    const url = this.utilService.getApiUrl('reservation/isAgencyTicketUsed');
    return this.http.post(url, dataObj)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }


  prepareReturnSeats(dataObj: any): Observable<any> {
    const url = this.utilService.getApiUrl('trip/prepareReturnSeats');
    return this.http.post(url, dataObj, {observe: 'response'})
      .map(
        (response: any) => {
          if (response.status === 0 || response.status === 200 && response.body && response.body.status === 0) {
            return response;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  numbering(tripId: any): Observable<boolean> {
    const url = this.utilService.getApiUrl('trip/numbering?tripId=' + tripId);
    return this.http.get(url)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  cancelTicket(dataObj: any): Observable<Invoice[]> {
    const url = this.utilService.getApiUrl('reservation/cancelTicket');
    return this.http.post(url, dataObj)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data.map(invoiceBE => Invoice.fromBE(invoiceBE));
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  setPaperType(paperType: string) {
    const url = this.utilService.getApiUrl('users/paper-type-set?paperType=' + paperType);
    return this.http.post(url, null)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data;
          } else {
            throw Observable.throw(response);
          }
        },
        (err) => {
          console.error(err);
        },
      );
  }

  cancelReservation(dataObj: any): Observable<Invoice[]> {
    const url = this.utilService.getApiUrl('reservation/cancelReservation');
    return this.http.post(url, dataObj)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data.map(invoiceBE => Invoice.fromBE(invoiceBE));
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  sendPaymentInfoEmail(email: string, eventType: string, reservationGroup: number[]): Observable<any> {
    const reservationIds = reservationGroup.map(id => `reservationIds=${id}`).join('&');
    const url = this.utilService.getApiUrl(
      'reservation/send-event-email?email=' + email + '&eventType=' + eventType + `&${reservationIds}`,
    );
    return this.http
      .get(url)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }


  seats(dataObj: any): Observable<Seat[]> {
    const url = this.utilService.getApiUrl('reservation/seats/');
    return this.http.post(url, dataObj)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data.map(value => new Seat(value));
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }


  downloadTicket(reservationId: string) {
    const url = this.utilService.getApiUrl('download/ticket?reservationId=' + reservationId);
    const req = new HttpRequest('GET', url, {
      reportProgress: true,
      responseType: 'arraybuffer',
    });

    return this.http.request(req);
  }

  downloadAllTicket(reservationId: string) {
    const url = this.utilService.getApiUrl('download/tickets?reservationGroup=' + reservationId);
    const req = new HttpRequest('GET', url, {
      reportProgress: true,
      responseType: 'arraybuffer',
    });

    return this.http.request(req);
  }


  downloadReservation(reservationId: string) {
    const url = this.utilService.getApiUrl('download/reservation?driveId=' + reservationId);
    const req = new HttpRequest('GET', url, {
      reportProgress: true,
      responseType: 'arraybuffer',
    });

    return this.http.request(req);
  }

  downloadReservations(reservationIds: string[]) {
    const params = reservationIds.map(id => `reservationIds=${id}`).join('&');
    const url = this.utilService.getApiUrl(`download/reservations?${params}`);
    const req = new HttpRequest('GET', url, {
      reportProgress: true,
      responseType: 'arraybuffer',
    });

    return this.http.request(req);
  }

  downloadReservationsTickets(reservationIds: string[]) {
    const params = reservationIds.map(id => `reservationIds=${id}`).join('&');
    const url = this.utilService.getApiUrl(`download/reservations/tickets?${params}`);
    const req = new HttpRequest('GET', url, {
      reportProgress: true,
      responseType: 'arraybuffer',
    });

    return this.http.request(req);
  }

  downloadInvoice(reservationGroup: string) {
    const url = this.utilService.getApiUrl('download/invoice?reservationGroup=' + reservationGroup);
    const req = new HttpRequest('GET', url, {
      reportProgress: true,
      responseType: 'arraybuffer',
      withCredentials: true,
    });

    return this.http.request(req);
  }

  downloadInvoiceByInvoiceId(invoiceId: string) {
    const url = this.utilService.getApiUrl('download/invoice/history?invoiceId=' + invoiceId);
    const req = new HttpRequest('GET', url, {
      reportProgress: true,
      responseType: 'arraybuffer',
      withCredentials: true,
    });

    return this.http.request(req);
  }

  downloadReservationInvoice(reservationData: string) {
    const url = this.utilService.getApiUrl('download/invoice/reservation?reservationInvoice=' + reservationData);
    const req = new HttpRequest('GET', url, {
      reportProgress: true,
      responseType: 'arraybuffer',
    });

    return this.http.request(req);
  }

  // Report


  reportUserActivity(dataObj: any): Observable<any> {
    const url = this.utilService.getApiUrl('report/userActivity');
    return this.http.post(url, dataObj)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return new UserActivityReport(response.data);
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => error,
      );
  }

  reportUserActivityXls(dataObj: any) {
    const url = this.utilService.getApiUrl('report/userActivity/xlsx');
    const req = new HttpRequest('POST', url, dataObj, {
      reportProgress: true,
      responseType: 'arraybuffer',
    });

    return this.http.request(req);
  }

  reportUserActivityPdf(dataObj: any, details: boolean) {
    const url = this.utilService.getApiUrl('report/userActivity/pdf?details=' + details);
    const req = new HttpRequest('POST', url, dataObj, {
      reportProgress: true,
      responseType: 'arraybuffer',
    });

    return this.http.request(req);
  }

  companyBusinessPremises(): Observable<any> {
    const url = this.utilService.getApiUrl('report/companyBusinessPremises');
    return this.http.get(url)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  getReportUsers(): Observable<any> {
    const url = this.utilService.getApiUrl('report/users');
    return this.http.get(url)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data.map(value => new CurrentUser(value));
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  availabilityReport(dataObj: any): Observable<any> {
    const url = this.utilService.getApiUrl('report/availability');
    return this.http.post(url, dataObj)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data.map(value => new AvailabilityReport(value));
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  availabilityReportXls(dataObj: any) {
    const url = this.utilService.getApiUrl('report/availability/xlsx');
    const req = new HttpRequest('POST', url, dataObj, {
      reportProgress: true,
      responseType: 'arraybuffer',
    });

    return this.http.request(req);
  }

  // RETURN API CALLS

  addSegment(dataObj: any): Observable<any> {
    const url = this.utilService.getApiUrl('reservation/addSegment');
    return this.http.post(url, dataObj)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return new CreateReservationsResponse(response.data[0]);
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  addReturnTickets(dataObj: any): Observable<any> {
    const url = this.utilService.getApiUrl('reservation/addReturnTickets');
    return this.http.post(url, dataObj)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  carrierDiscount(reservationGroupId: any): Observable<any> {
    const url = this.utilService.getApiUrl('reservation/carrier/discount');
    return this.http.post(url, {
      reservationGroupId,
    })
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  ticketFees(reservationGroupId: any): Observable<any> {
    const url = this.utilService.getApiUrl('reservation/booking/ticketFees');
    return this.http.post(url, {
      reservationGroupId,
    })
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  stationTerminalFees(data: any): Observable<any> {
    const url = this.utilService.getApiUrl('reservation/stationTerminal/fee');
    return this.http.post(url, data)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response;
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  getAvailableCurrencies(): Observable<Currency[]> {
    const url = this.utilService.getApiUrl('currency/available');
    return this.http.get(url)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data.currencies.map(value => new Currency(value));
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => {
          return error;
        },
      );
  }

  getVirtualPrivilegedVoyageCards() {
    const url = this.utilService.getApiUrl('reservation/virtual-privileged-voyage-cards');
    return this.http.get(url)
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data.map(card => new PrivilegedVoyageCard(card));
          } else {
            throw Observable.throw(response);
          }
        },
        (error: any) => error,
      );
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/ticket-booking.service.ts